import { z } from 'zod'

import { preprocessSpecialtyFieldDef } from '@/lib/agents/specialtyFieldDefaults'

import type {
  AdapterCatalogItem,
  AdapterFieldDefinition,
  AgentAdapterConfig,
  AgentMcpBinding,
  AgentSharedConfig,
  Agent,
  AgentAdapterType,
  AgentExecutionConfig,
  AgentExecutionMode,
  AgentSpecialtyConfig,
  AgentStatus,
  AthenaRecommendation,
  AuditActorType,
  AuditLogEntry,
  AppUser,
  AuthSession,
  ChannelType,
  Company,
  CouncilApproval,
  CouncilApprovalStatus,
  CouncilRequestType,
  CustomerType,
  Goal,
  Heartbeat,
  InputType,
  CompanyMcpValue,
  LlmModel,
  McpEmbeddedFieldDefinition,
  McpTool,
  Plugin,
  PluginInstance,
  PluginManifest,
  Project,
  ProjectStatus,
  RagDocumentCategoria,
  RagDocumentConfidencialidade,
  RagDocumentDepartamento,
  RagDocumentStatus,
  RagUploadResponse,
  Routine,
  RoutineStatus,
  Run,
  RunStatus,
  OutputType,
  SupplierType,
  Task,
  TaskExecutorType,
  TaskOperationType,
  TaskStatus,
  Transcript,
  TranscriptEntry,
  User,
  UserRole,
  WorkflowResponsavel,
  SipocCompany,
  SipocSector,
  SipocPosition,
  SipocProcess,
  SipocProcessStatus,
  SipocComponent,
  SipocComponentType,
  SipocValidationStatus,
  SipocResearchResult,
  DecisionMaker,
  ProspectProfile,
  ResearchTemplate,
  CnpjLookupResult,
  EstimatedEffort,
  RecommendationCitation,
  RecommendationKind,
  RecommendationStatus,
} from '@/types/api'

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

// Datas chegam da API como strings ISO-8601 (PRD §4 + Regra de Ouro #3).
// Mantemos string em runtime e formatamos só na borda da UI.
const isoDate = z.string().datetime({ offset: true })

/** Regra de Ouro #3 — validação estrita de datetime no wire. */
export const wireDateTime = isoDate

function coerceToIsoDateTimeOrNull(value: unknown): string | null | undefined {
  if (value === undefined) return undefined
  if (value === null) return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value
    const date = new Date(ms)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
  const raw = String(value).trim()
  if (!raw) return null
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/** Aceita ISO válido; normaliza unix/string inválida para null (defesa no front). */
export const wireDateTimeOptional = z.preprocess(
  coerceToIsoDateTimeOrNull,
  wireDateTime.nullable().optional(),
)

/** Postgres/Supabase timestamptz (`YYYY-MM-DD HH:MM:SS.ffffff+00`) → ISO com offset (Regra #3). */
export const wireDateTimeCoerced = z.string().transform((raw, ctx) => {
  const normalized = coerceToIsoDateTimeOrNull(raw)
  const candidate = normalized ?? (raw.trim() || null)
  if (!candidate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid datetime' })
    return z.NEVER
  }
  const parsed = isoDate.safeParse(candidate)
  if (!parsed.success) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid datetime' })
    return z.NEVER
  }
  return parsed.data
})

// `z.unknown()` infere o campo como opcional (`payload?: unknown`) por causa de
// um quirk do Zod 3. Para casar com tipos do domínio que declaram `payload: unknown`
// (presença obrigatória, valor arbitrário), usamos um custom que aceita qualquer
// valor mas mantém o campo no tipo inferido.
const unknownRequired = z.custom<unknown>(() => true)

// Tipa um schema garantindo que `z.infer<>` bate com o tipo do domínio.
// Se o tipo divergir, isso explode em compile time — evita drift silencioso.
const typed = <T>() => <S extends z.ZodType<T>>(schema: S) => schema

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export const agentStatusSchema = typed<AgentStatus>()(
  z.enum(['idle', 'working', 'offline', 'errored', 'paused']),  // Wave 1B
)

// adapter_type é texto livre no backend (VEC-319 dropou o CHECK constraint).
// O frontend só usa para display/badge — sem switch exhaustive — então aceitar
// string livre evita drift sempre que um novo provider entra no DB.
export const agentAdapterTypeSchema: z.ZodType<AgentAdapterType> = z
  .string()
  .min(1)

// execution_mode é catalog-driven (vectraclip.agent_execution_modes).
// Backend dropou o Literal — aceitamos string livre e validamos contra o
// catálogo via GET /api/agent-execution-modes (mesmo padrão de adapter_type).
export const agentExecutionModeSchema: z.ZodType<AgentExecutionMode> = z
  .string()
  .min(1)

export const agentExecutionConfigFieldSchema = z.object({
  key: z.string(),
  type: z.enum([
    'text',
    'textarea',
    'number',
    'boolean',
    'secret',
    'select',
    'multiselect',
  ]),
  label: z.string(),
  description: z.string().optional(),
  default: z.unknown().optional(),
  required: z.boolean().optional(),
  options: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
})

export const agentExecutionModeCatalogItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  displayOrder: z.number(),
  configSchema: z.array(agentExecutionConfigFieldSchema).default([]),
  isActive: z.boolean(),
})

export const agentExecutionModeCatalogListSchema = z.array(
  agentExecutionModeCatalogItemSchema,
)

export const taskStatusSchema = typed<TaskStatus>()(
  z.enum([
    'backlog',
    'queued',
    'in_progress',
    'review',
    'done',
    'blocked',
    'errored',  // Wave 1B (AUDIT-011)
  ]),
)

// operation_type é texto livre no backend — novos tipos entram via Edge
// Functions sem migration. Frontend usa para display/badge com fallback
// humanizado em `formatTaskOperationType`, então aceitar string livre evita
// drift sempre que um novo executor é criado.
export const taskOperationTypeSchema: z.ZodType<TaskOperationType> = z
  .string()
  .min(1)

export const taskExecutorTypeSchema = typed<TaskExecutorType>()(
  z.enum(['harness', 'managed_agent', 'auto']),
)

export const auditActorTypeSchema = typed<AuditActorType>()(
  z.enum(['human', 'agent', 'system']),
)

export const councilRequestTypeSchema = typed<CouncilRequestType>()(
  z.enum(['hire_agent', 'strategy', 'budget_increase', 'task_done']),
)

export const userRoleSchema = typed<UserRole>()(
  z.enum([
    'admin',
    'platform_admin',
    'consultant',
    'company_admin',
    'sector_responsible',
    'viewer',
    'member',
  ]),
)

export const councilApprovalStatusSchema = typed<CouncilApprovalStatus>()(
  z.enum(['pending', 'approved', 'rejected']),
)

export const projectStatusSchema = typed<ProjectStatus>()(
  z.enum(['backlog', 'planned', 'in_progress', 'completed', 'canceled']),
)

export const routineStatusSchema = typed<RoutineStatus>()(
  z.enum(['active', 'paused', 'error']),
)

export const runStatusSchema = typed<RunStatus>()(
  z.enum(['running', 'succeeded', 'failed', 'cancelled']),
)

// -----------------------------------------------------------------------------
// Entidades
// -----------------------------------------------------------------------------

export const companySchema = typed<Company>()(
  z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    mission: z.string(),
    ownerUserId: z.string().min(1),
    slug: z.string().min(1),
    members: z.array(
      z.object({
        userId: z.string().min(1),
        role: userRoleSchema,
        joinedAt: wireDateTimeCoerced,
      }),
    ),
    createdAt: wireDateTimeCoerced,
  }),
)

export const agentSchema = typed<Agent>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    name: z.string().min(1),
    role: z.string(),
    reportsToId: z.string().min(1).nullable(),
    status: agentStatusSchema,
    tokenBudget: z.number().int().nonnegative(),
    currentBurnRate: z.number().nonnegative(),
    adapterType: agentAdapterTypeSchema,
    specialtyId: z.string().min(1).nullable(),
    systemPrompt: z.string().nullable(),
    requiresApproval: z.boolean(),
    platformUrl: z.string().nullable(),
    isSystem: z.boolean(),
    createdAt: isoDate,
  }),
)

export const agentExecutionConfigSchema = typed<AgentExecutionConfig>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    agentId: z.string().min(1),
    executionMode: agentExecutionModeSchema,
    triggerConfig: z.record(z.unknown()),
    functionUrl: z.string().url().nullable(),
    authSecretRef: z.string().min(1).nullable(),
    authHeaderName: z.string().min(1).nullable(),
    isActive: z.boolean(),
    createdAt: isoDate,
    updatedAt: isoDate,
  }),
)

export const agentExecutionConfigListSchema = z.array(agentExecutionConfigSchema)

export const taskSchema = typed<Task>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    assignedToAgentId: z.string().min(1).nullable(),
    parentTaskId: z.string().min(1).nullable(),
    title: z.string().min(1),
    description: z.string(),
    operationType: taskOperationTypeSchema,
    status: taskStatusSchema,
    budgetLimit: z.number().nonnegative(),
    spent: z.number().nonnegative(),
    costUsd: z.number().nonnegative(),
    claimedAt: isoDate.nullable(),
    goalId: z.string().min(1).nullable(),
    workflowStepId: z.string().min(1).nullable(),
    outputJson: z.record(z.unknown()).nullable().optional(),
    inputJson: z.record(z.unknown()).nullable().optional(),
    approvedAt: isoDate.nullable().optional(),
    approvedByUserId: z.string().min(1).nullable().optional(),
    createdAt: isoDate,
    executorType: taskExecutorTypeSchema,
    managedAgentSessionId: z.string().min(1).nullable(),
    executorSelectedAt: isoDate.nullable(),
    executorRationale: z.string().nullable(),
    evaluationNotes: z.string().nullable().optional(),
  }),
)

export const goalSchema = z.object({
  id: z.string().min(1),
  companyId: z.string().min(1),
  parentGoalId: z.string().min(1).nullable(),
  title: z.string().min(1),
  metric: z.string(),
  target: z.number(),
  current: z.number(),
  kind: z.string().nullable().default(null),
  confidence: z.number().nullable().default(null),
  businessCaseStrength: z.string().nullable().default(null),
  classifiedAt: wireDateTimeOptional.default(null),
  classificationRationale: z.string().nullable().default(null),
  nextHandlerSuggested: z.string().nullable().default(null),
}) as unknown as z.ZodType<Goal>

export const heartbeatSchema = typed<Heartbeat>()(
  z.object({
    id: z.string().min(1),
    agentId: z.string().min(1),
    taskId: z.string().min(1).nullable(),
    status: agentStatusSchema,
    tokensUsed: z.number().int().nonnegative(),
    logExcerpt: z.string(),
    createdAt: isoDate,
  }),
)

// Os dois schemas abaixo têm `payload: unknown`. Com `exactOptionalPropertyTypes`
// + Zod 3, o TS infere o campo como `payload?: unknown`, divergindo do tipo do
// domínio (que exige presença). Como o validador em runtime já garante a presença
// via `unknownRequired`, fazemos cast explícito para o tipo do domínio. Isso
// preserva o tipo de retorno de `safeParse(...).data`.
function normalizeAuditLogKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    companyId: obj.companyId ?? obj.company_id,
    actorType: obj.actorType ?? obj.actor_type,
    actorId: obj.actorId ?? obj.actor_id,
    action: obj.action,
    target: obj.target,
    payload: obj.payload,
    createdAt: obj.createdAt ?? obj.created_at,
  }
}

export const auditLogEntrySchema = z.preprocess(
  normalizeAuditLogKeys,
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    actorType: auditActorTypeSchema,
    actorId: z.string().min(1),
    action: z.string().min(1),
    target: z.string(),
    payload: unknownRequired,
    createdAt: isoDate,
  }),
) as unknown as z.ZodType<AuditLogEntry>

// O backend Python (OpenClaw) retorna os campos em snake_case. O preprocessor
// normaliza para camelCase antes da validação, mantendo compatibilidade tanto
// com o MSW (já camelCase) quanto com a API real.
function normalizeApprovalKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    companyId: obj.companyId ?? obj.company_id,
    requestType: obj.requestType ?? obj.request_type,
    payload: obj.payload,
    status: obj.status,
    approvedByUserId:
      'approvedByUserId' in obj ? obj.approvedByUserId : obj.approved_by_user_id,
    createdAt: obj.createdAt ?? obj.created_at,
  }
}

export const councilApprovalSchema = z.preprocess(
  normalizeApprovalKeys,
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    requestType: councilRequestTypeSchema,
    payload: unknownRequired,
    status: councilApprovalStatusSchema,
    approvedByUserId: z.string().min(1).nullable(),
    createdAt: isoDate,
  }),
) as unknown as z.ZodType<CouncilApproval>

export const projectSchema = typed<Project>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    name: z.string().min(1),
    mission: z.string(),
    status: projectStatusSchema,
    leadAgentId: z.string().min(1).nullable(),
    targetDate: isoDate.nullable(),
    issueCompletionPct: z.number().min(0).max(100),
    createdAt: isoDate,
  }),
)

export const routineScheduleSchema = typed<Routine['schedule']>()(
  z.object({
    cron: z.string().min(1),
    timezone: z.string().min(1),
    human: z.string().min(1),
  }),
)

export const routineSchema = z.object({
  id: z.string().min(1),
  companyId: z.string().min(1),
  name: z.string().min(1),
  status: routineStatusSchema,
  schedule: routineScheduleSchema,
  agentId: z.string().min(1).nullable(),
  workflowDefinitionId: z.string().min(1).nullable().default(null),
  nextRunAt: isoDate.nullable(),
  lastRunAt: isoDate.nullable(),
  createdAt: isoDate,
}) as unknown as z.ZodType<Routine>

export const pluginSchema = typed<Plugin>()(
  z.object({
    id: z.string().min(1),
    slug: z.string().min(1),
    name: z.string().min(1),
    description: z.string(),
    author: z.string(),
    category: z.enum(['workflow', 'analytics', 'integration', 'utility']),
    version: z.string().min(1),
    installedByDefault: z.boolean(),
  }),
)

export const pluginManifestSchema = typed<PluginManifest>()(
  z.object({
    pluginId: z.string().min(1),
    requestedPermissions: z.array(z.string()),
    readme: z.string(),
    settingsSchema: z.record(z.unknown()),
  }),
)

export const pluginInstanceSchema = typed<PluginInstance>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    pluginId: z.string().min(1),
    enabled: z.boolean(),
    installedAt: isoDate,
    config: z.record(z.unknown()),
  }),
)

export const runSchema = typed<Run>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    agentId: z.string().min(1).nullable(),
    taskId: z.string().min(1).nullable(),
    status: runStatusSchema,
    startedAt: isoDate,
    finishedAt: isoDate.nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
  }),
)

export const transcriptEntrySchema = typed<TranscriptEntry>()(
  z.object({
    id: z.string().min(1),
    runId: z.string().min(1),
    role: z.enum(['system', 'user', 'assistant', 'tool']),
    message: z.string(),
    createdAt: isoDate,
  }),
)

export const transcriptSchema = typed<Transcript>()(
  z.object({
    runId: z.string().min(1),
    entries: z.array(transcriptEntrySchema),
  }),
)

// -----------------------------------------------------------------------------
// WorkflowStep — VEC-200
// -----------------------------------------------------------------------------

export const channelTypeSchema = typed<ChannelType>()(
  z.enum([
    'whatsapp', 'ui', 'email', 'upload', 'api',
    'siscomex', 'webhook', 'database', 'filesystem', 'manual',
  ]),
)

export const supplierTypeSchema = typed<SupplierType>()(
  z.enum(['agente', 'humano', 'sistema_externo', 'etapa_anterior']),
)

export const inputTypeSchema = typed<InputType>()(
  z.enum(['arquivo', 'dado_estruturado', 'evento', 'confirmacao_humana']),
)

export const outputTypeSchema = typed<OutputType>()(
  z.enum(['arquivo', 'dado_estruturado', 'notificacao', 'confirmacao']),
)

export const customerTypeSchema = typed<CustomerType>()(
  z.enum(['etapa_seguinte', 'humano', 'sistema_externo', 'cliente_final']),
)

export const workflowResponsavelSchema = typed<WorkflowResponsavel>()(
  z.enum(['agente', 'humano', 'sistema']),
)

const workflowSupplierSchema = z.object({
  nome: z.string().min(1),
  tipo: supplierTypeSchema,
  referencia: z.string().optional(),
})

const workflowInputSchema = z.object({
  nome: z.string().min(1),
  tipo: inputTypeSchema,
  formato: z.string().optional(),
  obrigatorio: z.boolean(),
  canal: channelTypeSchema,
  canalDetalhe: z.string().optional(),
})

const workflowOutputSchema = z.object({
  nome: z.string().min(1),
  tipo: outputTypeSchema,
  formato: z.string().optional(),
  destino: z.string().optional(),
  canal: channelTypeSchema,
  canalDetalhe: z.string().optional(),
})

const workflowCustomerSchema = z.object({
  nome: z.string().min(1),
  tipo: customerTypeSchema,
  referencia: z.string().optional(),
  canalAprovacao: channelTypeSchema.optional(),
})

const workflowDecisionSchema = z.object({
  condicao: z.string().min(1),
  acao: z.string().min(1),
  proximoStep: z.string().optional(),
})

const cadenceTriggerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('time_absolute'), delayHours: z.number().nonnegative() }),
  z.object({ kind: z.literal('time_after_response'), delayMinutes: z.number().nonnegative() }),
  z.object({ kind: z.literal('time_after_no_response'), delayHours: z.number().nonnegative() }),
])

export const cadenceMetaSchema = z.object({
  spinPhase: z.enum(['situation', 'problem', 'implication', 'need_payoff', 'followup', 'reengagement', 'archive']),
  channel: z.enum(['instagram_dm', 'whatsapp', 'email', 'sms']),
  trigger: cadenceTriggerSchema,
  scriptTemplate: z.string(),
  expectedResponse: z.enum(['opened_message', 'replied', 'clicked_link', 'none']).optional(),
})

const howMuchBudgetSchema = z.object({
  maxTokensOut: z.number().int().nonnegative().optional(),
  maxApiCalls: z.number().int().nonnegative().optional(),
  maxCostUsd: z.number().nonnegative().optional(),
  maxRetries: z.number().int().nonnegative().optional(),
  description: z.string().optional(),
})

const fiveW2HSchema = z.object({
  what: z.string().optional(),
  why: z.string().optional(),
  who: z.string().optional(),
  where: z.string().optional(),
  when: z.string().optional(),
  how: z.string().optional(),
  howMuch: howMuchBudgetSchema.optional(),
})

// Note: no typed<>() wrapper here because exactOptionalPropertyTypes causes
// incompatibility between Zod's optional() (T | undefined) and TypeScript
// optional fields (?:). Runtime validation is sufficient.
export const workflowStepSchema = z.object({
  id: z.string().min(1),
  companyId: z.string().min(1),
  stepCode: z.string().min(1),
  nome: z.string().min(1),
  descricao: z.string(),
  responsavel: workflowResponsavelSchema,
  ferramentas: z.array(z.string()),
  slaHoras: z.number().positive().nullable(),
  alertas: z.array(z.string()),
  proximo: z.array(z.string()),
  suppliers: z.array(workflowSupplierSchema),
  inputs: z.array(workflowInputSchema),
  outputs: z.array(workflowOutputSchema),
  customers: z.array(workflowCustomerSchema),
  decisions: z.array(workflowDecisionSchema),
  cadence: cadenceMetaSchema.optional(),
  fiveW2H: fiveW2HSchema.optional(),
  validationStatus: z.enum(['verde', 'amarelo', 'vermelho']).optional(),
  validationErrors: z.array(z.object({
    field: z.string(),
    message: z.string(),
  })).optional(),
  contractVersion: z.enum(['v1', 'v2']).optional(),
  createdAt: isoDate,
})

export const workflowStepListSchema = z.array(workflowStepSchema)

// -----------------------------------------------------------------------------
// Auth (VEC-140)
// -----------------------------------------------------------------------------

export const userSchema = typed<User>()(
  z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    email: z.string().email(),
    role: userRoleSchema,
    companyId: z.string().min(1),
    avatarUrl: z.string().nullable(),
    createdAt: isoDate,
  }),
)

// Backend pode trazer outros campos (createdAt, avatarUrl, etc.) que ignoramos
// silenciosamente via `.passthrough()` — só validamos o que a UI consome.
export const appUserSchema = typed<AppUser>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    name: z.string(),
    email: z.string().email(),
    role: userRoleSchema,
    assignedPositionId: z.string().min(1).nullable(),
  }),
)

export const appUserListSchema = z.array(appUserSchema)

export const authSessionSchema = typed<AuthSession>()(
  z.object({
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
    expiresAt: isoDate,
    user: userSchema,
  }),
)

// -----------------------------------------------------------------------------
// Listas (resposta padrão dos GETs de coleção)
// -----------------------------------------------------------------------------

export const companyListSchema = z.array(companySchema)
export const agentListSchema = z.array(agentSchema)
export const taskListSchema = z.array(taskSchema)
export const goalListSchema = z.array(goalSchema)
export const heartbeatListSchema = z.array(heartbeatSchema)
export const auditLogListSchema = z.array(auditLogEntrySchema)
export const councilApprovalListSchema = z.array(councilApprovalSchema)
export const projectListSchema = z.array(projectSchema)
export const routineListSchema = z.array(routineSchema)
export const pluginListSchema = z.array(pluginSchema)
export const pluginManifestListSchema = z.array(pluginManifestSchema)
export const pluginInstanceListSchema = z.array(pluginInstanceSchema)
export const runListSchema = z.array(runSchema)
// workflowStepListSchema already exported above (defined before Auth section)

const intelligenceTrendSchema = z.enum(['up', 'down', 'stable'])

export const intelligenceDashboardSchema = z.object({
  totals: z.object({
    companies: z.number(),
    activeAgents: z.number(),
    tasksThisWeek: z.number(),
    tokensThisMonth: z.number(),
    successRate: z.number(),
  }),
  companies: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      activeAgents: z.number(),
      tasksThisWeek: z.number(),
      tokensThisWeek: z.number(),
      trend: intelligenceTrendSchema,
    }),
  ),
  weeklyTasks: z.array(z.object({ week: z.string(), tasks: z.number() })),
  weeklyTokens: z.array(z.object({ week: z.string(), tokens: z.number() })),
  burnByCompany: z.array(z.record(z.union([z.string(), z.number()]))),
  burnSeriesKeys: z.array(z.string()),
  taskStatusPie: z.array(z.object({ status: z.string(), count: z.number() })),
  runDurationHistogram: z.array(
    z.object({ bucket: z.string(), count: z.number() }),
  ),
  activityHeatmap: z.array(
    z.object({
      companyId: z.string(),
      companyName: z.string(),
      weekday: z.string(),
      count: z.number(),
    }),
  ),
  topAgentsByProductivity: z.array(
    z.object({
      agentId: z.string(),
      agentName: z.string(),
      companyId: z.string(),
      companyName: z.string(),
      tasksDone: z.number(),
    }),
  ),
  topAgentsByCost: z.array(
    z.object({
      agentId: z.string(),
      agentName: z.string(),
      companyId: z.string(),
      companyName: z.string(),
      costUsd: z.number(),
    }),
  ),
})

// -----------------------------------------------------------------------------
// Adapter Catalog (VEC-235)
// -----------------------------------------------------------------------------

export const adapterFieldTypeSchema = z.enum([
  'text',
  'textarea',
  'number',
  'boolean',
  'select',
  'multiselect',
  'secret',
  'url',
  'file_upload',
])

/** Seed migration may store a bare array (CLI model aliases) — normalize to object. */
export const adapterFieldOptionsJsonSchema = z.preprocess(
  (value: unknown) => {
    if (Array.isArray(value)) return { values: value }
    return value
  },
  z.record(z.unknown()).nullable(),
)

// Sem typed<>(): preprocess em optionsJson diverge _input com exactOptionalPropertyTypes.
export const adapterRuntimeProfileFieldTemplateSchema = z.object({
  fieldKey: z.string().min(1),
  fieldLabel: z.string().min(1),
  fieldType: adapterFieldTypeSchema,
  isRequired: z.boolean(),
  optionsJson: adapterFieldOptionsJsonSchema,
  sortOrder: z.number().int().nonnegative(),
})

export const adapterRuntimeProfileSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  defaultProvider: z.string().min(1),
  fieldDefinitionsTemplate: z.array(adapterRuntimeProfileFieldTemplateSchema),
  displayOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: wireDateTime,
  updatedAt: wireDateTime,
})
export const adapterRuntimeProfileListSchema = z.array(adapterRuntimeProfileSchema)

// Sem typed<>(): runtimeProfileId optional + transform omite chave quando ausente (exactOptionalPropertyTypes).
export const adapterCatalogItemSchema = z
  .object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    slug: z.string().min(1),
    displayName: z.string().min(1),
    provider: z.string().min(1),
    runtimeProfileId: z.string().min(1).optional(),
    isActive: z.boolean(),
    createdAt: wireDateTime,
  })
  .transform((row): AdapterCatalogItem => {
    const base = {
      id: row.id,
      companyId: row.companyId,
      slug: row.slug,
      displayName: row.displayName,
      provider: row.provider,
      isActive: row.isActive,
      createdAt: row.createdAt,
    }
    if (row.runtimeProfileId !== undefined) {
      return { ...base, runtimeProfileId: row.runtimeProfileId }
    }
    return base
  })
export const adapterCatalogListSchema = z.array(adapterCatalogItemSchema)

export const createAdapterFromProfileBodySchema = z.object({
  runtimeProfileId: z.string().min(1),
  displayName: z.string().min(1),
  isActive: z.boolean().optional(),
})

export const adapterFieldDefinitionSchema = typed<AdapterFieldDefinition>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    adapterId: z.string().min(1),
    fieldKey: z.string().min(1),
    fieldLabel: z.string().min(1),
    fieldType: adapterFieldTypeSchema,
    isRequired: z.boolean(),
    optionsJson: z.record(z.unknown()).nullable(),
    triggerCondition: z.record(z.unknown()).nullable(),
    sortOrder: z.number().int().nonnegative(),
    isActive: z.boolean(),
  }),
)
export const adapterFieldListSchema = z.array(adapterFieldDefinitionSchema)

export const agentAdapterConfigSchema = typed<AgentAdapterConfig>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    agentId: z.string().min(1),
    adapterId: z.string().min(1),
    fieldValuesJson: z.record(z.unknown()),
    isActive: z.boolean(),
    createdAt: isoDate,
  }),
)

export const agentSharedConfigSchema = typed<AgentSharedConfig>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    agentId: z.string().min(1),
    values: z.record(z.unknown()),
    schema: z.array(z.record(z.unknown())),
    createdAt: isoDate.nullable(),
    updatedAt: isoDate.nullable(),
  }),
)

// -----------------------------------------------------------------------------
// MCP servers + agent bindings
// -----------------------------------------------------------------------------

const mcpEmbeddedFieldSchema = z
  .object({
    fieldKey: z.string().min(1),
    fieldLabel: z.string().optional(),
    fieldType: adapterFieldTypeSchema,
    isRequired: z.boolean().optional(),
    optionsJson: z.record(z.unknown()).nullable().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
  })
  .transform((raw): McpEmbeddedFieldDefinition => {
    const out: McpEmbeddedFieldDefinition = {
      fieldKey: raw.fieldKey,
      fieldType: raw.fieldType,
      optionsJson: raw.optionsJson ?? null,
    }
    if (raw.fieldLabel !== undefined) out.fieldLabel = raw.fieldLabel
    if (raw.isRequired !== undefined) out.isRequired = raw.isRequired
    if (raw.sortOrder !== undefined) out.sortOrder = raw.sortOrder
    return out
  })

export const mcpServerCatalogItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  transport: z.enum(['stdio', 'http', 'sse', 'credential_only']),
  endpointUrlTemplate: z.string().nullable(),
  authType: z.enum(['oauth2_client_credentials', 'api_key', 'bearer', 'none', 'env_vars']),
  fieldDefinitions: z.array(mcpEmbeddedFieldSchema),
  category: z.enum([
    'bpm',
    'messaging',
    'code',
    'crm',
    'storage',
    'finance',
    'automation',
    'other',
  ]),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  displayOrder: z.number().int(),
  isActive: z.boolean(),
  documentationUrl: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
})
export const mcpServerCatalogListSchema = z.array(mcpServerCatalogItemSchema)

export const mcpToolSchema = typed<McpTool>()(
  z.object({
    name: z.string().min(1),
    description: z.string().nullable(),
    inputSchema: z.record(z.unknown()),
  }),
)

export const agentMcpBindingSchema = typed<AgentMcpBinding>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    agentId: z.string().min(1),
    mcpServerId: z.string().min(1),
    fieldValuesJson: z.record(z.unknown()),
    allowedTools: z.array(z.string()).nullable(),
    toolsCache: z.array(mcpToolSchema).nullable(),
    lastHealthAt: isoDate.nullable(),
    lastError: z.string().nullable(),
    isActive: z.boolean(),
    createdAt: isoDate,
    updatedAt: isoDate,
  }),
)
export const agentMcpBindingListSchema = z.array(agentMcpBindingSchema)

export const mcpHandshakeResultSchema = z.object({
  tools: z.array(mcpToolSchema),
  healthAt: isoDate.optional(),
})

export const mcpToolsRefreshResultSchema = z.object({
  tools: z.array(mcpToolSchema),
})

export const companyMcpValueSchema = typed<CompanyMcpValue>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    mcpServerId: z.string().min(1),
    profileKey: z.string().min(1),
    fieldValuesJson: z.record(z.unknown()),
    allowedTools: z.array(z.string()).nullable(),
    isActive: z.boolean(),
    createdAt: isoDate,
    updatedAt: isoDate,
  }),
)
export const companyMcpValueListSchema = z.array(companyMcpValueSchema)

// -----------------------------------------------------------------------------
// LLM Models + Agent Specialties (VEC-201 / VEC-203)
// -----------------------------------------------------------------------------

export const llmModelSchema = typed<LlmModel>()(
  z.object({
    id: z.string().min(1),
    provider: z.string().min(1),
    displayName: z.string().min(1),
    inputCostPer1M: z.number().nonnegative(),
    outputCostPer1M: z.number().nonnegative(),
    cacheReadCostPer1M: z.number().nonnegative(),
    contextWindowK: z.number().int().positive(),
    isActive: z.boolean(),
    effectiveFrom: z.string().min(1),
  }),
)
export const llmModelListSchema = z.array(llmModelSchema)

// Sem typed<>(): defaults no Zod deixam _input com campos opcionais vs AgentDomain.
export const agentDomainSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().default(''),
  icon: z.string().default(''),
  color: z.string().default(''),
  displayOrder: z.number().int().default(100),
  isActive: z.boolean().default(true),
})
export const agentDomainListSchema = z.array(agentDomainSchema)

// Tipos de campo vêm do catálogo/backend (agent_specialties.config_schema).
// Não fechar enum no front — novos tipos (textarea, multiselect) entram sem quebrar parse.
export const specialtyFieldTypeSchema = z.enum([
  'text',
  'textarea',
  'secret',
  'boolean',
  'number',
  'select',
  'multiselect',
  'json',
])

const specialtyFieldDefaultValueSchema = z.union([
  z.string(),
  z.boolean(),
  z.number(),
  z.array(z.string()),
])

export const specialtyFieldDefSchema = z.preprocess(
  preprocessSpecialtyFieldDef,
  z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    type: specialtyFieldTypeSchema,
    required: z.boolean(),
    defaultValue: specialtyFieldDefaultValueSchema.optional(),
    options: z.array(z.string()).optional(),
    placeholder: z.string().optional(),
  }),
)

const agentSpecialtyStatusSchema = z.enum([
  'active',
  'deprecated',
  'experimental',
  'community',
])
const agentSpecialtySourceSchema = z.enum([
  'internal',
  'import_csv',
  'athena',
  'skillforge',
])

export const agentSpecialtySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  domain: z.string().min(1),
  description: z.string(),
  compatibleRoles: z.array(z.string()),
  systemPromptTemplate: z.string(),
  configSchema: z.array(specialtyFieldDefSchema).nullable().default(null),
  isActive: z.boolean(),
  /** S3-A — defaults when API omits (pre-migration rows / explicit select). */
  status: agentSpecialtyStatusSchema.default('active'),
  source: agentSpecialtySourceSchema.default('internal'),
})
export const agentSpecialtyListSchema = z.array(agentSpecialtySchema)

export const agentSpecialtyConfigSchema = typed<AgentSpecialtyConfig>()(
  z.object({
    id: z.string().min(1),
    agentId: z.string().min(1),
    specialtyId: z.string().min(1),
    values: z.record(z.unknown()),
    updatedAt: isoDate,
  }),
)
export const agentSpecialtyConfigListSchema = z.array(agentSpecialtyConfigSchema)

// -----------------------------------------------------------------------------
// SIPOC Builder v1.0 (VEC-246)
// -----------------------------------------------------------------------------

export const sipocProcessStatusSchema = typed<SipocProcessStatus>()(
  z.enum(['rascunho', 'em_revisao', 'aprovado', 'arquivado']),
)

export const sipocComponentTypeSchema = typed<SipocComponentType>()(
  z.enum(['supplier', 'input', 'activity', 'output', 'customer']),
)

export const sipocValidationStatusSchema = typed<SipocValidationStatus>()(
  z.enum(['verde', 'amarelo', 'vermelho']),
)

export const sipocLogicPatternSchema = z.enum([
  'SIMPLE',
  'SPLIT',
  'MERGE',
  'LOOP-WHILE',
  'LOOP-FOR-EACH',
  'WAIT-EVENT',
  'WAIT-TIME',
  'SUBFLOW',
  'ERROR-HANDLER',
  'FORCE-FAIL',
  'MANUAL',
])

export const sipocComponentContentSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  // 5W2H
  what: z.string().optional(),
  why: z.string().optional(),
  who: z.string().optional(),
  where: z.string().optional(),
  when: z.string().optional(),
  how: z.string().optional(),
  howMuch: z.string().optional(),
  // Automação
  logicPattern: sipocLogicPatternSchema.optional(),
  automationScore: z.number().min(0).max(100).optional(),
  automationJustification: z.string().optional(),
})

export const sipocCompanySchema = typed<SipocCompany>()(
  z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    logoUrl: z.string().nullable(),
    website: z.string().nullable(),
    metadata: z.record(z.unknown()),
    createdAt: isoDate,
    updatedAt: isoDate,
  }),
)

const sipocProcessSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  sectorName: z.string().nullable().optional(),
})

export const sipocSectorSchema = z.object({
  id: z.string().min(1),
  companyId: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  icon: z.string().nullable(),
  parentSectorId: z.string().min(1).nullable(),
  metadata: z.record(z.unknown()),
  createdAt: isoDate,
  updatedAt: isoDate,
  processes: z.array(sipocProcessSummarySchema).optional(),
}) as unknown as z.ZodType<SipocSector>

export const sipocPositionSchema = typed<SipocPosition>()(
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    sectorId: z.string().min(1).nullable(),
    title: z.string().min(1),
    description: z.string().nullable(),
    reportsToId: z.string().min(1).nullable(),
    metadata: z.record(z.unknown()),
    createdAt: isoDate,
    updatedAt: isoDate,
  }),
)

export const sipocComponentSchema = typed<SipocComponent>()(
  z.object({
    id: z.string().min(1),
    processId: z.string().min(1),
    type: sipocComponentTypeSchema,
    content: sipocComponentContentSchema,
    order: z.number().int(),
    validationStatus: sipocValidationStatusSchema,
    validationNotes: z.string().nullable(),
    metadata: z.record(z.unknown()),
    createdAt: isoDate,
    updatedAt: isoDate,
  }),
)

export const sipocProcessSchema = z.object({
    id: z.string().min(1),
    sectorId: z.string().min(1),
    positionId: z.string().min(1).nullable(),
    name: z.string().min(1),
    description: z.string().nullable(),
    status: sipocProcessStatusSchema,
    version: z.number().int(),
    responsibleId: z.string().min(1).nullable(),
    metadata: z.record(z.unknown()),
    createdAt: isoDate,
    updatedAt: isoDate,
    components: z.array(sipocComponentSchema).default([]),
  }) as unknown as z.ZodType<SipocProcess>

export const sipocCompanyListSchema = z.array(sipocCompanySchema)
export const sipocSectorListSchema = z.array(sipocSectorSchema)

export const sipocPromotionResultSchema = z.object({
  success: z.boolean(),
  agent_id: z.string().optional(),
  routine_id: z.string().optional(),
  score: z.number().optional(),
})
export const sipocPositionListSchema = z.array(sipocPositionSchema)
export const sipocProcessListSchema = z.array(sipocProcessSchema)
export const sipocComponentListSchema = z.array(sipocComponentSchema)

export const sipocResearchResultSchema = typed<SipocResearchResult>()(
  z.object({
    setor: z.string().min(1),
    processosSugeridos: z.array(
      z.object({
        nome: z.string().min(1),
        descricao: z.string(),
        sipocBase: z.object({
          suppliers: z.array(z.string()),
          inputs: z.array(z.string()),
          outputs: z.array(z.string()),
          customers: z.array(z.string()),
        }),
        automacaoScoreEstimado: z.number().min(0).max(100),
      }),
    ),
    riscosComuns: z.array(z.string()),
    oportunidadesIa: z.array(z.string()),
  }),
)

// -----------------------------------------------------------------------------
// RAG / Knowledge Base (VEC-370)
// -----------------------------------------------------------------------------
//
// O backend `/api/companies/{id}/rag/upload` retorna snake_case (document_id,
// task_id) porque é um dict cru, não Pydantic com alias. Convertemos para
// camelCase no schema via `.transform()` para manter o frontend consistente.

export const ragDocumentStatusSchema = typed<RagDocumentStatus>()(
  z.enum(['uploaded', 'processing', 'indexed', 'failed']),
)

export const ragDocumentCategoriaSchema = typed<RagDocumentCategoria>()(
  z.enum(['manual', 'procedimento', 'contrato', 'tabela', 'email', 'outro']),
)

export const ragDocumentDepartamentoSchema = typed<RagDocumentDepartamento>()(
  z.enum(['operacao', 'comercial', 'financeiro', 'rh', 'juridico', 'ti']),
)

export const ragDocumentConfidencialidadeSchema =
  typed<RagDocumentConfidencialidade>()(
    z.enum(['publica', 'interna', 'restrita']),
  )

// Schema com transform (ZodEffects) — input snake_case do backend, output
// camelCase para o frontend. Tipo anotado com input != output explícito.
export const ragUploadResponseSchema = z
  .object({
    document_id: z.string().min(1),
    task_id: z.string().min(1).nullable(),
    status: ragDocumentStatusSchema,
    duplicate: z.boolean().optional().default(false),
    filename: z.string().min(1),
  })
  .transform(
    (raw): RagUploadResponse => ({
      documentId: raw.document_id,
      taskId: raw.task_id,
      status: raw.status,
      duplicate: raw.duplicate,
      filename: raw.filename,
    }),
  )

// -----------------------------------------------------------------------------
// Prospect Profiles (VEC-334)
// -----------------------------------------------------------------------------

export const decisionMakerSchema = z.object({
  nome: z.string().min(1),
  cargo: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  linkedin: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  fonte: z.string().nullable().optional(),
}) as unknown as z.ZodType<DecisionMaker>

export const prospectProfileSchema = z.object({
  id: z.string().min(1),
  companyId: z.string().min(1),
  nomeRazaoSocial: z.string().nullable().optional(),
  cnpj: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  setor: z.string().nullable().optional(),
  endereco: z.record(z.string()).nullable().optional(),
  telefone: z.string().nullable().optional(),
  emailContato: z.string().nullable().optional(),
  decisores: z.array(decisionMakerSchema).nullable().optional(),
  sourceTaskId: z.string().nullable().optional(),
  enrichedAt: z.string().nullable().optional(),
  rawResearch: z.string().nullable().optional(),
  artifacts: z.record(z.unknown()).nullable().optional(),
  createdAt: isoDate,
  updatedAt: isoDate,
  // Pesquisa Oracle (VEC-XXX)
  tipo: z.string().nullable().optional(),
  linkedinUrl: z.string().nullable().optional(),
  instagramHandle: z.string().nullable().optional(),
  extraUrls: z.array(z.object({ label: z.string().optional(), url: z.string() })).nullable().optional(),
  cnpjLookupData: z.record(z.unknown()).nullable().optional(),
  qsa: z.array(z.record(z.unknown())).nullable().optional(),
  researchTemplateId: z.string().nullable().optional(),
  researchStatus: z.string().nullable().optional(),
  researchProgress: z.record(z.unknown()).nullable().optional(),
  researchCronExpr: z.string().nullable().optional(),
  nextResearchAt: z.string().nullable().optional(),
  lastResearchAt: z.string().nullable().optional(),
}) as unknown as z.ZodType<ProspectProfile>

export const prospectProfileListSchema = z.array(prospectProfileSchema)

// -----------------------------------------------------------------------------
// Research Templates
// -----------------------------------------------------------------------------

export const researchTemplateSchema = z.object({
  id: z.string().min(1),
  companyId: z.string().nullable().optional(),
  slug: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  promptTemplate: z.string(),
  outputSections: z.array(z.string()).default([]),
  defaultUrls: z.array(z.string()).default([]),
  requireReview: z.boolean().default(true),
  active: z.boolean().default(true),
  thinkingLevel: z.enum(['minimal', 'low', 'medium', 'high']).optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
}) as unknown as z.ZodType<ResearchTemplate>

export const researchTemplateListSchema = z.array(researchTemplateSchema)

// -----------------------------------------------------------------------------
// CNPJ Lookup result
// -----------------------------------------------------------------------------

export const cnpjPartnerSchema = z.object({
  name: z.string(),
  role: z.string().nullable(),
  role_code: z.string().nullable(),
  document: z.string().nullable(),
  entry_date: z.string().nullable(),
  country: z.string().nullable(),
  age_range: z.string().nullable(),
})

export const cnpjLookupResultSchema = z.object({
  cnpj: z.string(),
  name: z.string().nullable(),
  trade_name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  address_number: z.string().nullable(),
  address_complement: z.string().nullable(),
  address_neighborhood: z.string().nullable(),
  zip_code: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  legal_nature: z.string().nullable(),
  legal_nature_code: z.string().nullable(),
  company_size: z.string().nullable(),
  opening_date: z.string().nullable(),
  registration_status: z.string().nullable(),
  registration_status_date: z.string().nullable(),
  registration_status_reason: z.string().nullable(),
  efr: z.string().nullable(),
  cnae_main_code: z.string().nullable(),
  cnae_main_description: z.string().nullable(),
  cnaes_secondary: z.array(z.object({ codigo: z.string(), descricao: z.string() })).default([]),
  share_capital: z.number().nullable(),
  partners: z.array(cnpjPartnerSchema).default([]),
}) as unknown as z.ZodType<CnpjLookupResult>

// -----------------------------------------------------------------------------
// Athena Recommendations (VEC-408 / VEC-409)
// -----------------------------------------------------------------------------
//
// Backend Python retorna snake_case; preprocessor normaliza para camelCase
// antes da validação Zod (mesmo padrão de `councilApprovalSchema`). Para os
// dois campos JSON arbitrários (`proposedChangesJson` e `citations`),
// validamos shape externo e deixamos o interior cru — componentes específicos
// por kind fazem o narrowing via `RecommendationOfKind<K>`.

// 8 valores — espelha ATHENA-RECOMMENDATIONS.md + CHECK athena_recommendations_kind_check
export const recommendationKindSchema = typed<RecommendationKind>()(
  z.enum([
    'hire_new_agent',
    'add_specialty',
    'rewrite_system_prompt',
    'create_specialty',
    'consolidate_agents',
    'diagnose_gap',
    'suggest_automation',
    'suggest_hire_agent',
  ]),
)

export const recommendationStatusSchema = typed<RecommendationStatus>()(
  z.enum(['pending', 'approved', 'applied', 'rejected', 'superseded']),
)

export const estimatedEffortSchema = typed<EstimatedEffort>()(
  z.enum(['S', 'M', 'L', 'XL']),
)

function normalizeCitation(raw: unknown): RecommendationCitation | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  return {
    chunkId: (o.chunkId ?? o.chunk_id) as string | undefined,
    page: o.page as number | undefined,
    source: o.source as string | undefined,
    textExcerpt: (o.textExcerpt ?? o.text_excerpt) as string | undefined,
  }
}

export const recommendationCitationSchema = z.preprocess(
  (raw) => normalizeCitation(raw),
  z.object({
    chunkId: z.string().optional(),
    page: z.number().int().optional(),
    source: z.string().optional(),
    textExcerpt: z.string().optional(),
  }),
) as unknown as z.ZodType<RecommendationCitation>

function normalizeAthenaRecommendationKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    companyId: obj.companyId ?? obj.company_id,
    kind: obj.kind,
    status: obj.status,
    targetAgentId: obj.targetAgentId ?? obj.target_agent_id ?? null,
    targetSpecialtyId:
      obj.targetSpecialtyId ?? obj.target_specialty_id ?? null,
    triggeredByGoalId:
      obj.triggeredByGoalId ?? obj.triggered_by_goal_id ?? null,
    triggeredByTaskId:
      obj.triggeredByTaskId ?? obj.triggered_by_task_id ?? null,
    title: obj.title,
    rationale: obj.rationale ?? '',
    proposedChangesJson:
      obj.proposedChangesJson ?? obj.proposed_changes_json ?? {},
    citations: Array.isArray(obj.citations) ? obj.citations : [],
    confidence: obj.confidence,
    estimatedEffort: obj.estimatedEffort ?? obj.estimated_effort,
    reviewedByUserId:
      obj.reviewedByUserId ?? obj.reviewed_by_user_id ?? null,
    reviewedAt: obj.reviewedAt ?? obj.reviewed_at ?? null,
    reviewNotes: obj.reviewNotes ?? obj.review_notes ?? null,
    appliedHistoryId:
      obj.appliedHistoryId ?? obj.applied_history_id ?? null,
    createdAt: obj.createdAt ?? obj.created_at,
    updatedAt: obj.updatedAt ?? obj.updated_at,
  }
}

export const athenaRecommendationSchema = z.preprocess(
  normalizeAthenaRecommendationKeys,
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    kind: recommendationKindSchema,
    status: recommendationStatusSchema,
    targetAgentId: z.string().min(1).nullable(),
    targetSpecialtyId: z.string().min(1).nullable(),
    triggeredByGoalId: z.string().min(1).nullable(),
    triggeredByTaskId: z.string().min(1).nullable(),
    title: z.string().min(1),
    rationale: z.string(),
    proposedChangesJson: z.record(z.unknown()),
    citations: z.array(recommendationCitationSchema),
    confidence: z.number().min(0).max(1),
    estimatedEffort: estimatedEffortSchema,
    reviewedByUserId: z.string().min(1).nullable(),
    reviewedAt: isoDate.nullable(),
    reviewNotes: z.string().nullable(),
    appliedHistoryId: z.string().min(1).nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
  }),
) as unknown as z.ZodType<AthenaRecommendation>

export const athenaRecommendationListSchema = z.array(
  athenaRecommendationSchema,
)

// -----------------------------------------------------------------------------
// Workflow definitions (PR-F2 — canvas n8n-style + Trigger types)
// -----------------------------------------------------------------------------
//
// Backend Python retorna **snake_case** no GET (`trigger_type`, `cron_expression`,
// `is_scheduled`, etc.) e aceita **camelCase** no PUT (Pydantic alias). Os
// preprocessors abaixo normalizam para camelCase ANTES da validação, então
// o consumer só vê camelCase. Mesma estratégia de `athenaRecommendationSchema`.

/** Catalog-driven (Regra Ouro #2) — slugs vêm de workflow_trigger_types. */
const workflowTriggerTypeSlugSchema = z.string().min(1)

function normalizeTriggerTypeKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    slug: obj.slug,
    name: obj.name,
    description: obj.description ?? '',
    icon: obj.icon ?? '',
    displayOrder: obj.displayOrder ?? obj.display_order ?? 0,
    isActive: obj.isActive ?? obj.is_active ?? true,
  }
}

export const workflowTriggerTypeSchema = z.preprocess(
  normalizeTriggerTypeKeys,
  z.object({
    slug: workflowTriggerTypeSlugSchema,
    name: z.string().min(1),
    description: z.string(),
    icon: z.string(),
    displayOrder: z.number(),
    isActive: z.boolean(),
  }),
)

export const workflowTriggerTypeListSchema = z.array(workflowTriggerTypeSchema)

function normalizeLogicPatternKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    category: obj.category ?? '',
    taxonomy: obj.taxonomy ?? '',
    name: obj.name,
    description: obj.description ?? '',
    heuristics: Array.isArray(obj.heuristics) ? obj.heuristics : [],
    icon: obj.icon ?? '',
    color: obj.color ?? '',
    displayOrder: obj.displayOrder ?? obj.display_order ?? 0,
    jsonSkeleton: obj.jsonSkeleton ?? obj.json_skeleton ?? null,
    engineHandler: obj.engineHandler ?? obj.engine_handler ?? '',
    isActive: obj.isActive ?? obj.is_active ?? true,
  }
}

export const workflowLogicPatternSchema = z.preprocess(
  normalizeLogicPatternKeys,
  z.object({
    id: z.string().min(1),
    category: z.string(),
    taxonomy: z.string(),
    name: z.string().min(1),
    description: z.string(),
    heuristics: z.array(z.string()),
    icon: z.string(),
    color: z.string(),
    displayOrder: z.number(),
    jsonSkeleton: z.record(z.unknown()).nullable(),
    engineHandler: z.string(),
    isActive: z.boolean(),
  }),
)

export const workflowLogicPatternListSchema = z.array(workflowLogicPatternSchema)

// ----- W15.1 catalogs: agent-skills + operation-types-catalog -----

function normalizeAgentSkillKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  const values = obj.values
  const opFromValues =
    values &&
    typeof values === 'object' &&
    Array.isArray((values as Record<string, unknown>).operation_types)
      ? ((values as Record<string, unknown>).operation_types as unknown[])
      : null
  return {
    id: obj.id,
    agentId: obj.agentId ?? obj.agent_id,
    agentName: obj.agentName ?? obj.agent_name,
    specialtySlug: obj.specialtySlug ?? obj.specialty_slug,
    specialtyName: obj.specialtyName ?? obj.specialty_name,
    operationTypes:
      obj.operationTypes ??
      obj.operation_types ??
      opFromValues ??
      [],
    values: values ?? undefined,
  }
}

export const agentSkillSchema = z.preprocess(
  normalizeAgentSkillKeys,
  z.object({
    id: z.string().uuid(),
    agentId: z.string().uuid(),
    agentName: z.string().min(1),
    specialtySlug: z.string().min(1),
    specialtyName: z.string().min(1),
    operationTypes: z.array(z.string()),
    values: z.record(z.unknown()).optional(),
  }),
)

export const agentSkillListSchema = z.array(agentSkillSchema)

function normalizeOperationTypeCatalogKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    name: obj.name,
    description: obj.description ?? null,
    category: obj.category ?? '',
    icon: obj.icon ?? null,
    color: obj.color ?? null,
    display_order: obj.display_order ?? obj.displayOrder ?? 0,
    primary_agent_id: obj.primary_agent_id ?? obj.primaryAgentId ?? null,
    default_specialty_slug:
      obj.default_specialty_slug ?? obj.defaultSpecialtySlug ?? null,
    is_active: obj.is_active ?? obj.isActive ?? true,
    routing_score: obj.routing_score ?? obj.routingScore ?? 0,
  }
}

export const operationTypeCatalogItemSchema = z.preprocess(
  normalizeOperationTypeCatalogKeys,
  z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    category: z.string(),
    icon: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    display_order: z.number(),
    primary_agent_id: z.string().nullable().optional(),
    default_specialty_slug: z.string().nullable().optional(),
    is_active: z.boolean(),
    routing_score: z.number(),
  }),
)

export const operationTypeCatalogListSchema = z.array(
  operationTypeCatalogItemSchema,
)

// ----- WorkflowDefinition (summary + detail) -----

function normalizeWorkflowDefinitionKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    slug: obj.slug,
    name: obj.name,
    description: obj.description ?? null,
    isActive: obj.isActive ?? obj.is_active ?? true,
    triggerType: obj.triggerType ?? obj.trigger_type ?? 'manual',
    cronExpression: obj.cronExpression ?? obj.cron_expression ?? null,
    isScheduled: obj.isScheduled ?? obj.is_scheduled ?? false,
    stepsCount: obj.stepsCount ?? obj.steps_count ?? 0,
    createdAt: obj.createdAt ?? obj.created_at,
    updatedAt: obj.updatedAt ?? obj.updated_at,
  }
}

export const workflowDefinitionSummarySchema = z.preprocess(
  normalizeWorkflowDefinitionKeys,
  z.object({
    id: z.string().min(1),
    slug: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable(),
    isActive: z.boolean(),
    triggerType: workflowTriggerTypeSlugSchema,
    cronExpression: z.string().nullable(),
    isScheduled: z.boolean(),
    stepsCount: z.number(),
    createdAt: isoDate,
    updatedAt: isoDate,
  }),
)

export const workflowDefinitionSummaryListSchema = z.array(
  workflowDefinitionSummarySchema,
)

// ----- WorkflowStepRich -----

function normalizeWorkflowStepRichKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  // `proximoStepCodes` pode chegar como array de strings ou como objeto
  // (alguns endpoints retornam `proximo_step_codes` ou aninhado em `proximo`).
  const proxRaw =
    obj.proximoStepCodes ?? obj.proximo_step_codes ?? obj.proximo ?? []
  const proximoStepCodes = Array.isArray(proxRaw)
    ? proxRaw.filter((x): x is string => typeof x === 'string')
    : []
  return {
    id: obj.id,
    workflowId: obj.workflowId ?? obj.workflow_id ?? '',
    stepOrder: obj.stepOrder ?? obj.step_order ?? 0,
    slug: obj.slug ?? obj.stepCode ?? obj.step_code ?? '',
    name: obj.name ?? obj.nome ?? '',
    description: obj.description ?? obj.descricao ?? null,
    specialtySlug:
      obj.specialtySlug ??
      obj.specialty_slug ??
      obj.defaultAssignedSpecialtySlug ??
      obj.default_assigned_specialty_slug ??
      null,
    logicPattern: obj.logicPattern ?? obj.logic_pattern ?? 'SIMPLE',
    responsavel: obj.responsavel ?? 'agente',
    currentOperationType:
      obj.currentOperationType ?? obj.current_operation_type ?? null,
    nextOperationType:
      obj.nextOperationType ?? obj.next_operation_type ?? null,
    defaultOperationType:
      obj.defaultOperationType ?? obj.default_operation_type ?? null,
    proximoStepCodes,
    onSuccessStepId: obj.onSuccessStepId ?? obj.on_success_step_id ?? null,
    onFailureAction:
      obj.onFailureAction ?? obj.on_failure_action ?? 'block',
    requiresApproval:
      obj.requiresApproval ?? obj.requires_approval ?? false,
    active: obj.active ?? true,
    slaHoras: obj.slaHoras ?? obj.sla_horas ?? null,
    setor: obj.setor ?? null,
    triggerType: obj.triggerType ?? obj.trigger_type ?? 'realtime',
    triggerConfig:
      typeof obj.triggerConfig === 'object' && obj.triggerConfig !== null
        ? (obj.triggerConfig as Record<string, unknown>)
        : typeof obj.trigger_config === 'object' && obj.trigger_config !== null
          ? (obj.trigger_config as Record<string, unknown>)
          : {},
    agentSpecialtyConfigId:
      obj.agentSpecialtyConfigId ??
      obj.agent_specialty_config_id ??
      null,
    sipocMeta: (() => {
      const raw = obj.sipocMeta ?? obj.sipoc_meta
      if (typeof raw !== 'object' || raw === null) return null
      const m = raw as Record<string, unknown>
      const linked =
        m.linkedSipocComponentId ?? m.linked_sipoc_component_id ?? null
      const assignee = m.assigneePositionId ?? m.assignee_position_id ?? null
      return {
        source: typeof m.source === 'string' ? m.source : undefined,
        nodeId:
          typeof m.nodeId === 'string'
            ? m.nodeId
            : typeof m.node_id === 'string'
              ? m.node_id
              : undefined,
        nodeType:
          typeof m.nodeType === 'string'
            ? m.nodeType
            : typeof m.node_type === 'string'
              ? m.node_type
              : undefined,
        linkedSipocComponentId:
          typeof linked === 'string' && linked.trim() ? linked.trim() : null,
        assigneePositionId:
          typeof assignee === 'string' && assignee.trim()
            ? assignee.trim()
            : null,
      }
    })(),
  }
}

export const workflowStepRichSchema = z.preprocess(
  normalizeWorkflowStepRichKeys,
  z.object({
    id: z.string().min(1),
    workflowId: z.string(),
    stepOrder: z.number(),
    slug: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable(),
    specialtySlug: z.string().nullable(),
    logicPattern: z.string(),
    responsavel: workflowResponsavelSchema,
    currentOperationType: z.string().nullable(),
    nextOperationType: z.string().nullable(),
    defaultOperationType: z.string().nullable(),
    proximoStepCodes: z.array(z.string()),
    onSuccessStepId: z.string().nullable(),
    onFailureAction: z.enum(['block', 'retry', 'skip']),
    requiresApproval: z.boolean(),
    active: z.boolean(),
    slaHoras: z.number().nullable(),
    setor: z.string().nullable(),
    triggerType: workflowTriggerTypeSlugSchema.optional(),
    triggerConfig: z.record(z.unknown()).optional(),
    agentSpecialtyConfigId: z.string().nullable().optional(),
    sipocMeta: z
      .object({
        source: z.string().optional(),
        nodeId: z.string().optional(),
        nodeType: z.string().optional(),
        linkedSipocComponentId: z.string().nullable().optional(),
        assigneePositionId: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    cadence: cadenceMetaSchema.optional(),
  }),
)

export const workflowStepRichListSchema = z.array(workflowStepRichSchema)

// ----- Detail envelope { workflow, steps } -----

function normalizeWorkflowDetailKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    workflow: obj.workflow ?? obj,
    steps: Array.isArray(obj.steps) ? obj.steps : [],
  }
}

export const workflowDefinitionDetailSchema = z.preprocess(
  normalizeWorkflowDetailKeys,
  z.object({
    workflow: workflowDefinitionSummarySchema,
    steps: workflowStepRichListSchema,
  }),
)

// ----- Upsert response { workflow, steps } — mesma forma -----

export const upsertWorkflowDefinitionResponseSchema =
  workflowDefinitionDetailSchema

// ----- Run workflow result -----

function normalizeRunWorkflowParentKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    status: obj.status,
    operationType: obj.operationType ?? obj.operation_type,
    title: obj.title,
  }
}

function normalizeRunWorkflowSubtaskKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    status: obj.status,
    operationType: obj.operationType ?? obj.operation_type,
    assignedToAgentId:
      obj.assignedToAgentId ?? obj.assigned_to_agent_id ?? null,
    title: obj.title,
  }
}

function normalizeRunWorkflowResultKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    parent: obj.parent,
    subtasks: Array.isArray(obj.subtasks) ? obj.subtasks : [],
  }
}

const runWorkflowParentSchema = z.preprocess(
  normalizeRunWorkflowParentKeys,
  z.object({
    id: z.string().min(1),
    status: z.string(),
    operationType: z.string(),
    title: z.string(),
  }),
)

const runWorkflowSubtaskSchema = z.preprocess(
  normalizeRunWorkflowSubtaskKeys,
  z.object({
    id: z.string().min(1),
    status: z.string(),
    operationType: z.string(),
    assignedToAgentId: z.string().nullable(),
    title: z.string(),
  }),
)

export const runWorkflowResultSchema = z.preprocess(
  normalizeRunWorkflowResultKeys,
  z.object({
    parent: runWorkflowParentSchema,
    subtasks: z.array(runWorkflowSubtaskSchema),
  }),
)

// ----- BPMN gateway bindings (P0-FE-3) -----

export const bpmnGatewayBindingSchema = z.object({
  bpmnGatewayType: z.enum([
    'gateway_exclusive',
    'gateway_parallel',
    'gateway_inclusive',
  ]),
  topology: z.enum(['fork', 'join']),
  logicPatternId: z.string().min(1),
  name: z.string(),
  description: z.string(),
  engineStatus: z.enum(['active', 'pending']),
  displayOrder: z.number(),
  isActive: z.boolean(),
})

export const bpmnGatewayBindingListSchema = z.array(bpmnGatewayBindingSchema)
